import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Target, TrendingUp, Heart, ListChecks, Wand2, Clock, GitCompare } from "lucide-react";
import { Mascot } from "@/components/OwlMascot";
import { speakOwl, isOwlVoiceOn } from "@/lib/owlVoice";

const STAGES = [
  { key: "initial", label: "Inicial", color: "#2C49A0" },
  { key: "intermediate", label: "Intermedia", color: "#A43790" },
  { key: "final", label: "Final", color: "#05A859" },
];

const Section = ({ icon: Icon, title, color, children }) => (
  <div className="jasq-card p-5">
    <h2 className="font-display font-extrabold text-[#1F2547] flex items-center gap-2 mb-3" style={{ color }}>
      <Icon size={18} /> {title}
    </h2>
    {children}
  </div>
);

export default function EstudiantePlanIA() {
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [stage, setStage] = useState("initial");

  const load = async () => {
    try {
      const { data } = await api.get("/ai/learning-plan/me");
      setPlans(data.plans || {});
      const latest = data.plan?.stage;
      if (latest) setStage(latest);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/ai/learning-plan/me?stage=${stage}`);
      setPlans((p) => ({ ...p, [stage]: data.plan }));
      const msg = data.plan?.content?.mensaje_motivador;
      if (msg) { toast.success("¡Tu ruta está lista! 🎉"); if (isOwlVoiceOn()) speakOwl(msg); }
    } catch {
      toast.error("No se pudo generar la ruta. Intenta de nuevo.");
    } finally { setGenerating(false); }
  };

  const plan = plans[stage];
  const c = plan?.content || {};
  const anyPlan = Object.keys(plans).length > 0;

  return (
    <div className="space-y-6" data-testid="estudiante-plan-ia-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#A43790]">Plan Personalizado</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547] flex items-center gap-2">
            <Sparkles className="text-[#A43790]" /> Mi ruta de aprendizaje
          </h1>
          <p className="text-sm text-[#6E7591] mt-1">Tu ruta se crea según cómo aprendes y sientes, y evoluciona contigo: inicial, intermedia y final.</p>
        </div>
        <button onClick={generate} disabled={generating} data-testid="generate-plan-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#A43790] text-white font-bold text-sm hover:bg-[#D63384] transition-colors disabled:opacity-60">
          {generating ? <Loader2 size={18} className="animate-spin" /> : <Wand2 size={18} />}
          {plan ? "Actualizar esta ruta" : "Generar esta ruta"}
        </button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="plan-stage-tabs">
        {STAGES.map((s) => (
          <button key={s.key} onClick={() => setStage(s.key)} data-testid={`plan-stage-${s.key}`}
            className={`px-4 py-2 rounded-xl text-sm font-bold border-2 transition-colors ${stage === s.key ? "text-white" : "text-[#1F2547] border-[#E1E5F0]"}`}
            style={stage === s.key ? { backgroundColor: s.color, borderColor: s.color } : {}}>
            Ruta {s.label}{plans[s.key] ? " ✓" : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="jasq-card p-10 text-center text-sm text-[#6E7591]">Cargando…</div>
      ) : !plan ? (
        <div className="jasq-card p-10 text-center" data-testid="plan-empty">
          <Mascot message="¡Completa tus diagnósticos y crearé tu ruta de aprendizaje! 🦉" variant="info" size={72} />
          <p className="text-sm text-[#6E7591] mt-4">
            {anyPlan ? "Aún no tienes esta ruta. Se generará al completar el CEP y el CAD de esta etapa." : "Aún no tienes rutas. Se generan automáticamente al completar tus diagnósticos."}
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="plan-content">
          {c.resumen && (
            <div className="jasq-card p-5 bg-gradient-to-br from-[#FFF7DA] to-white border-[#FFCC28]">
              <p className="text-[#1F2547] font-medium">{c.resumen}</p>
              {c.mensaje_motivador && <p className="mt-2 text-[#A43790] font-bold flex items-center gap-1"><Sparkles size={14} /> {c.mensaje_motivador}</p>}
            </div>
          )}
          {c.evolucion && (
            <Section icon={GitCompare} title="Tu evolución" color="#05A859">
              <p className="text-sm text-[#4C5478] leading-relaxed">{c.evolucion}</p>
            </Section>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            {Array.isArray(c.fortalezas) && (
              <Section icon={TrendingUp} title="Tus fortalezas" color="#16A34A">
                <ul className="space-y-1.5 text-sm text-[#4C5478]">{c.fortalezas.map((x, i) => <li key={i} className="flex gap-2"><span className="text-[#16A34A]">●</span>{x}</li>)}</ul>
              </Section>
            )}
            {Array.isArray(c.areas_mejora) && (
              <Section icon={Target} title="Áreas para crecer" color="#2C49A0">
                <ul className="space-y-1.5 text-sm text-[#4C5478]">{c.areas_mejora.map((x, i) => <li key={i} className="flex gap-2"><span className="text-[#2C49A0]">●</span>{x}</li>)}</ul>
              </Section>
            )}
          </div>
          {Array.isArray(c.objetivos) && (
            <Section icon={ListChecks} title="Objetivos de las próximas semanas" color="#A43790">
              <ul className="space-y-1.5 text-sm text-[#4C5478]">{c.objetivos.map((x, i) => <li key={i} className="flex gap-2"><span className="text-[#A43790]">{i + 1}.</span>{x}</li>)}</ul>
            </Section>
          )}
          {Array.isArray(c.actividades) && (
            <Section icon={Sparkles} title="Actividades recomendadas" color="#D63384">
              <div className="grid sm:grid-cols-2 gap-3">
                {c.actividades.map((a, i) => (
                  <div key={i} className="border-2 border-[#F2F4FA] rounded-2xl p-3" data-testid={`activity-${i}`}>
                    <p className="font-bold text-[#1F2547]">{a.titulo}</p>
                    <p className="text-sm text-[#6E7591] mt-0.5">{a.descripcion}</p>
                    {a.duracion_min && <p className="text-xs text-[#A6ACC1] mt-1 flex items-center gap-1"><Clock size={11} /> {a.duracion_min} min</p>}
                  </div>
                ))}
              </div>
            </Section>
          )}
          {c.recomendacion_emocional && (
            <Section icon={Heart} title="Para tu bienestar" color="#D63384">
              <p className="text-sm text-[#4C5478]">{c.recomendacion_emocional}</p>
            </Section>
          )}
          <p className="text-xs text-[#A6ACC1] text-center">Generado el {plan.generated_at?.slice(0, 10)}</p>
        </div>
      )}
    </div>
  );
}
